import { CAMPAIGN_DRAWER_PARAM } from "@/pages/dashboard/campaigns/hooks/useCampaignDrawerParam"
import routes from "@/routes/routes"
import type { NotificationItem } from "@/store/notificationsAPI"

const FALLBACK_LOCALE = "en"
const UNKNOWN_TIME_LABEL = "—"

/** Maps the API's typed notification deep links to routes owned by Extranet. */
export function platformNotificationHref(deepLink: NotificationItem["deepLink"]): string | null {
  if (!deepLink) return null

  switch (deepLink.type) {
    case "booking":
    case "order": {
      // Open the campaign the notification is about, like the email's "Open campaign" link
      // (MPE-1507 item 13). Only notifications whose deep link carries a campaignId can; the rest
      // keep the Campaigns list until the backend adds it to their deep link.
      const campaignId = deepLink.params?.campaignId?.trim()
      return campaignId
        ? `${routes["dashboard/campaigns"]}?${new URLSearchParams({ [CAMPAIGN_DRAWER_PARAM]: campaignId })}`
        : routes["dashboard/campaigns"]
    }
    case "settings":
      return routes["dashboard/settings/notifications"]
    default:
      return null
  }
}

function notificationDate(iso: string): Date | null {
  if (typeof iso !== "string" || !iso.trim()) return null

  const date = new Date(iso)
  return Number.isFinite(date.getTime()) ? date : null
}

function createFormatter<T>(language: string, factory: (locale: string) => T): T | null {
  try {
    return factory(language || FALLBACK_LOCALE)
  } catch {
    try {
      return factory(FALLBACK_LOCALE)
    } catch {
      return null
    }
  }
}

export function notificationRelativeTime(iso: string, language: string): string | null {
  const date = notificationDate(iso)
  if (!date) return null

  const formatter = createFormatter(language, (locale) => new Intl.RelativeTimeFormat(locale, { numeric: "auto" }))
  if (!formatter) return null

  const elapsedMs = Date.now() - date.getTime()

  try {
    const seconds = Math.round(elapsedMs / 1000)
    if (seconds < 60) return formatter.format(-seconds, "second")
    const minutes = Math.round(seconds / 60)
    if (minutes < 60) return formatter.format(-minutes, "minute")
    const hours = Math.round(minutes / 60)
    if (hours < 24) return formatter.format(-hours, "hour")
    const days = Math.round(hours / 24)
    if (days < 30) return formatter.format(-days, "day")
    const months = Math.round(days / 30)
    if (months < 12) return formatter.format(-months, "month")
    return formatter.format(-Math.round(months / 12), "year")
  } catch {
    return null
  }
}

export function notificationDateTime(iso: string, language: string): string | null {
  const date = notificationDate(iso)
  if (!date) return null

  const formatter = createFormatter(
    language,
    (locale) =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }),
  )

  try {
    return formatter?.format(date) ?? null
  } catch {
    return null
  }
}

export function notificationTimestamp(iso: string, language: string): string {
  const relativeTime = notificationRelativeTime(iso, language)
  const dateTime = notificationDateTime(iso, language)

  return [relativeTime, dateTime].filter(Boolean).join(" · ") || UNKNOWN_TIME_LABEL
}
